import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { AuthService } from '../services/auth.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { User } from '../models/user';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard extends KeycloakAuthGuard {
    protected readonly router: Router;
    protected readonly keycloak: KeycloakService;
    protected readonly authService = inject(AuthService);
    protected readonly httpClient = inject(HttpClient);

    constructor() {
        const router = inject(Router);
        const keycloak = inject(KeycloakService);

        super(router, keycloak);

        this.router = router;
        this.keycloak = keycloak;
    }

    public async isAccessAllowed(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {
        if (!this.authenticated) {
            await this.keycloak.login({
                redirectUri: window.location.origin + state.url
            });
            return false;
        }

        // Optimisation: Si le user est déjà chargé (via cache ou précédent appel), on vérifie direct
        if (this.authService.user.getValue()) {
            return this.verifyUserAccess(route, state);
        } else {
            return this.fetchUserData(route, state);
        }
    }

    private fetchUserData(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.httpClient.get<any>('api/users/me/', { context: new HttpContext().set(NGX_LOADING_BAR_IGNORED, true) })
            .pipe(
                map(data => {
                    const userData = data as User;
                    this.authService.user.next(userData);
                    this.authService.cacheUser(userData); // Mise en cache pour les prochains démarrages

                    return this.verifyUserAccess(route, state);
                }),
                catchError(error => {
                    // IMPORTANT: ne JAMAIS renaviguer vers une route gardee ici.
                    // 'dashboard' est protege par ce meme guard : en cas d'echec de
                    // /me (backend 500, Keycloak injoignable, reseau coupe...), un
                    // navigate(['dashboard']) re-declenche fetchUserData -> /me ->
                    // echec -> navigate -> boucle infinie qui spamme /me.
                    if (error.status === 401) {
                        // Token rejete : relancer une authentification propre.
                        this.keycloak.login({ redirectUri: window.location.origin + state.url });
                        return of(false);
                    }
                    if (error.status === 0 && this.authService.user.value) {
                        // Reseau coupe alors qu'on avait une session : deconnexion.
                        this.authService.deconnexion();
                        return of(false);
                    }
                    // Autres erreurs (500 backend, etc.) : on stoppe sans reboucler.
                    // L'erreur reste visible une seule fois dans la console reseau.
                    console.error('[AuthGuard] Echec du chargement de /me, navigation interrompue', error);
                    return of(false);
                })
            ).toPromise();
    }

    private verifyUserAccess(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        // Pas de bypass is_superuser ici : les apps/groups d'un superuser reflètent ses
        // véritables appartenances aux groupes Django (voir User.get_applications()), qui
        // varient elles aussi selon le client sélectionné — un superuser doit donc être
        // redirigé au même titre qu'un utilisateur normal s'il perd l'accès à un module.
        if (!this.authService.aAcces({apps: route.data.apps, groups: route.data.groups})) {
            this.router.navigate(['dashboard'], { queryParams: { returnUrl: state.url } });
            return false;
        }
        return true;
    }
}
